import { DocumentClient, ETATS_A_REGLER } from './documents';
import { Mission, missionSignee } from './missions';

/* Situation financière d'une mission (maquette MissionDocuments, encadré
   « Situation de la mission »), calculée à partir des documents émis par
   Tribe et de la trace de signature. Montants TTC en F CFA.

   - Contrat : montant TTC du devis signé (trace serveur), sinon du dernier
     contrat émis ; inconnu tant que la mission n'est pas signée.
   - Facturé : factures non annulées, moins les avoirs qui ne portent pas sur
     une facture déjà annulée (sans quoi l'avoir serait compté deux fois).
   - Réglé : ce qui a été payé sur ces factures (montant moins reste dû).
   - Reste dû : reste dû des factures à régler.
   - Reste à facturer : contrat moins facturé, jamais négatif. */

export interface SituationFinanciere {
  contrat: number | null;
  facture: number;
  regle: number;
  resteDu: number;
  resteAFacturer: number | null;
}

const ttc = (d: DocumentClient) => Number(d.montantTtc) || 0;

export function montantContrat(m: Mission, documents: DocumentClient[]): number | null {
  if (m.signatureDevis?.montantTtc != null) return m.signatureDevis.montantTtc;
  const contrat = documents.filter(d => d.type === 'CONTRAT')
    .sort((a, b) => (b.emisLe || '').localeCompare(a.emisLe || ''))[0];
  if (contrat) return ttc(contrat);
  return null;
}

export function situationFinanciere(m: Mission, documents: DocumentClient[]): SituationFinanciere {
  const factures = documents.filter(d => d.type === 'FACTURE' && d.etatPaiement !== 'ANNULEE');
  const annulees = new Set(documents.filter(d => d.type === 'FACTURE' && d.etatPaiement === 'ANNULEE').map(d => d.numero));
  const avoirs = documents.filter(d => d.type === 'AVOIR' && !(d.documentOrigineNumero && annulees.has(d.documentOrigineNumero)));

  const facture = factures.reduce((t, d) => t + ttc(d), 0) - avoirs.reduce((t, d) => t + Math.abs(ttc(d)), 0);
  const resteDeLaFacture = (d: DocumentClient) => Math.max(0, d.resteDuTtc ?? (d.etatPaiement === 'PAYEE' ? 0 : ttc(d)));
  const regle = factures.reduce((t, d) => t + (ttc(d) - resteDeLaFacture(d)), 0);
  const resteDu = factures.filter(d => ETATS_A_REGLER.includes(d.etatPaiement || '')).reduce((t, d) => t + resteDeLaFacture(d), 0);
  const contrat = missionSignee(m) || m.signatureDevis ? montantContrat(m, documents) : null;

  return {
    contrat,
    facture,
    regle,
    resteDu,
    resteAFacturer: contrat != null ? Math.max(0, contrat - facture) : null,
  };
}
